import { useEffect, useState } from 'react';
import type { WhoamiResponse } from '@/api';
import { shortNpub } from '@/lib/npub';
import { useRustyState } from '@/state';

export interface AccountIdentity {
  /** The account email from the notification addresses, else the one the session reports. */
  email?: string;
  /** The SSO display name, when there is one. */
  name?: string;
  /** One line for the shell: the email, else the npub, else the name. */
  label?: string;
}

/** Who is signed in, from the backend rather than the stored session, which may lack the email. */
export function useAccountIdentity(): AccountIdentity {
  const emails = useRustyState((s) => s.verifiedEmails);
  const publicKey = useRustyState((s) => s.getSession()?.publicKey);
  const [whoami, setWhoami] = useState<WhoamiResponse | null>(null);

  useEffect(() => {
    let live = true;
    const { api, getAndSetVerifiedEmails } = useRustyState.getState();
    void getAndSetVerifiedEmails().catch(() => undefined);
    api.auth
      ?.whoami()
      .then((res) => live && setWhoami(res))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  const email = emails.find((e) => e.is_account_email)?.email ?? whoami?.email ?? undefined;
  const name = whoami?.display_name ?? undefined;
  const npub = publicKey ? shortNpub(publicKey) : undefined;
  return { email, name, label: email ?? npub ?? name };
}
